import moment from 'moment'
import firebase from 'firebase/compat/app'
import { difference, uniq } from 'lodash'

import TasksHelper, {
    GENERIC_COMMENT_TYPE,
    MAX_GOLD_TO_EARN_BY_COMMENT,
} from '../../../components/TaskListView/Utils/TasksHelper'
import { updateAssistantLastCommentData, resetAssistantLastCommentData } from '../Assistants/assistantsFirestore'
import {
    getGoalData,
    setGoalAssistant,
    updateGoalLastCommentData,
    resetGoalLastCommentData,
} from '../Goals/goalsFirestore'
import {
    getSkillData,
    setSkillAssistant,
    updateSkillLastCommentData,
    resetSkillLastCommentData,
} from '../Skills/skillsFirestore'
import {
    getArtifactData,
    setArtifactAssistant,
    updateArtifactLastCommentData,
    resetArtifactLastCommentData,
} from '../Artifacts/artifactsFirestore'
import {
    addFollowerWithoutFeeds,
    earnGold,
    getDb,
    getFirestoreTime,
    getId,
    getMentionedUsersIdsWhenEditText,
    getNote,
    getTaskData,
    globalWatcherUnsub,
    logEvent,
    runHttpsCallableFunction,
    tryAddFollower,
} from '../firestore'
import { FEED_PUBLIC_FOR_ALL } from '../../../components/Feeds/Utils/FeedsConstants'
import { setNoteAssistant, updateNoteLastCommentData, resetNoteLastCommentData } from '../Notes/notesFirestore'
import {
    createGenericTaskWhenMention,
    setTaskAssistant,
    updateTaskLastCommentData,
    rebuildTaskLastCommentData,
} from '../Tasks/tasksFirestore'
import store from '../../../redux/store'
import { awaitWriteAck } from '../offlineWriteAck'
import { commentOutbox, subscribeCommentOutbox } from './commentOutbox'
import { threadRunGate } from './threadRunGate'
import { commitCommentOnce } from './commitCommentOnce'
import {
    LAST_COMMENT_CHARACTER_LIMIT_IN_BIG_SCREEN,
    cleanTextMetaData,
    extractMediaContextFromText,
    removeFormatTagsFromText,
    shrinkTagText,
} from '../../../functions/Utils/parseTextUtils'
import {
    getAssistantInProject,
    isGlobalAssistant,
    resolveAssistantForProjectObject,
} from '../../../components/AdminPanel/Assistants/assistantsHelper'
import {
    resetUserLastCommentData,
    setUserAssistant,
    updateUserDataDirectly,
    updateUserLastCommentData,
} from '../Users/usersFirestore'
import {
    setContactAssistant,
    updateContactLastCommentData,
    resetContactLastCommentData,
} from '../Contacts/contactsFirestore'
import { getLinkedParentChatUrl } from '../../../components/ChatsView/Utils/ChatHelper'
import { selectAssistantEnabledFor } from '../../../components/ChatsView/Utils/assistantEnabledScope'
import { endBotSpinnerWait } from '../../../components/ChatsView/Utils/botSpinnerTrigger'
import { PROJECT_TYPE_GUIDE } from '../../../components/SettingsView/ProjectsSettings/ProjectsSettings'
import ProjectHelper from '../../../components/SettingsView/ProjectsSettings/ProjectHelper'
import { setProjectLastChatActionDate } from '../Projects/projectsFirestore'
import { getChatMeta, updateChatAssistant, updateChatData } from './chatsFirestore'
import { BatchWrapper } from '../../../functions/BatchWrapper/batchWrapper'
import {
    FOLLOWER_ARTIFACTS_TYPE,
    FOLLOWER_ASSISTANTS_TYPE,
    FOLLOWER_CONTACTS_TYPE,
    FOLLOWER_GOALS_TYPE,
    FOLLOWER_NOTES_TYPE,
    FOLLOWER_SKILLS_TYPE,
    FOLLOWER_TASKS_TYPE,
    FOLLOWER_TOPICS_TYPE,
    FOLLOWER_USERS_TYPE,
} from '../../../components/Followers/FollowerConstants'
import { generateUserIdsToNotifyForNewComments } from '../../assistantHelper'
import {
    ASSISTANT_LAST_COMMENT_ALL_PROJECTS_KEY,
    getChatNotificationWithCommentId,
    getProjectChatLastNotification,
} from './chatNotificationPriority'
import { buildCommentNotificationIdentity } from './commentNotificationHelper'
import { isNewComment, queueObjectActivityFeedUnreadClear } from '../Feeds/activityFeedReadState'
import { queueSummaryChatNotificationPatch, subscribeActivitySummary } from '../Feeds/activityUnreadSummary'

export { ASSISTANT_LAST_COMMENT_ALL_PROJECTS_KEY, getProjectChatLastNotification }

const isActiveProjectUserId = (projectId, userId) => {
    const { loggedUser } = store.getState()
    return !!userId && (userId === loggedUser.uid || !!TasksHelper.getUserInProject(projectId, userId))
}

const filterActiveProjectUserIds = (projectId, userIds, context) => {
    const validUserIds = uniq((userIds || []).filter(userId => isActiveProjectUserId(projectId, userId)))
    const skippedUserIds = difference(uniq((userIds || []).filter(Boolean)), validUserIds)

    // A follower who has since left the project is the ordinary case here, not an anomaly:
    // the metadata is simply saved without them. Worth seeing while developing, noise in
    // a production console.
    if (__DEV__ && skippedUserIds.length > 0) {
        console.warn('[TaskComments] Ignoring stale project user ids while saving comment metadata', {
            projectId,
            context,
            skippedUserIds,
        })
    }

    return validUserIds
}

export const watchComments = (projectId, chatType, chatId, watcherKey, amountCommentsToGet, callback, options) => {
    globalWatcherUnsub[watcherKey] = getDb()
        .collection(`chatComments/${projectId}/${chatType}/${chatId}/comments`)
        .orderBy('created', 'desc')
        .limit(amountCommentsToGet)
        .onSnapshot(
            ...(options ? [{ includeMetadataChanges: true }] : []),
            snapshot => {
                const comments = []
                snapshot.forEach(doc => {
                    comments.push({ ...doc.data(), id: doc.id })
                })
                callback(comments, snapshot.metadata)
            },
            ...(options?.onError ? [options.onError] : [])
        )
}

export async function getChatCommentsWithLinkedEmails(projectId, chatType, chatId) {
    if (!projectId || !chatType || !chatId) return []
    const snapshot = await getDb().collection(`chatComments/${projectId}/${chatType}/${chatId}/comments`).get()
    return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })).filter(comment => comment?.gmailData?.messageId)
}

export function watchChatNotifications(projectId, userId, watcherKey, callback) {
    // Served from the user's one activity summary listener when it is live for this project; the
    // direct per-project listener is the fallback (see Feeds/activityUnreadSummary.js).
    globalWatcherUnsub[watcherKey] = subscribeActivitySummary({
        userId,
        projectId,
        kind: 'chats',
        onData: callback,
        legacySubscribe: deliver =>
            getDb()
                .collection(`chatNotifications/${projectId}/${userId}`)
                .onSnapshot(snapshot => {
                    const notifications = []
                    snapshot.forEach(doc => {
                        notifications.push(getChatNotificationWithCommentId(doc))
                    })
                    deliver(notifications)
                }),
    })
}

export const getParentObjectData = async (projectId, objectId, objectType) => {
    let isPublicFor = null
    let assistantId = ''
    let followObjectsType = null
    let object = null
    let parentObjectCreatorId = ''
    let title = ''

    switch (objectType) {
        case 'tasks':
            await getTaskData(projectId, objectId).then(task => {
                if (!task) return
                object = task
                isPublicFor = task.isPublicFor
                assistantId = task.assistantId
                parentObjectCreatorId = task.creatorId
                title = task.extendedName
                followObjectsType = FOLLOWER_TASKS_TYPE
            })
            break
        case 'notes':
            await getNote(projectId, objectId).then(note => {
                if (!note) return
                object = note
                isPublicFor = note.isPublicFor
                assistantId = note.assistantId
                parentObjectCreatorId = note.creatorId
                title = note.extendedTitle
                followObjectsType = FOLLOWER_NOTES_TYPE
            })
            break
        case 'topics':
            await getChatMeta(projectId, objectId).then(chat => {
                if (!chat) return
                object = chat
                isPublicFor = chat.isPublicFor
                assistantId = chat.assistantId
                parentObjectCreatorId = chat.creatorId
                title = chat.title
                followObjectsType = FOLLOWER_TOPICS_TYPE
            })
            break
        case 'users':
        case 'contacts':
            const user = TasksHelper.getUserInProject(projectId, objectId)
            const objectIsUser = !!user
            const contact = user || TasksHelper.getContactInProject(projectId, objectId)
            if (!contact) break
            object = contact
            isPublicFor = contact.isPublicFor
            assistantId = contact.assistantId
            parentObjectCreatorId = objectIsUser ? objectId : contact.recorderUserId
            title = contact.displayName
            followObjectsType = objectIsUser ? FOLLOWER_USERS_TYPE : FOLLOWER_CONTACTS_TYPE
            break
        case 'goals':
            await getGoalData(projectId, objectId).then(goal => {
                if (!goal) return
                object = goal
                isPublicFor = goal.isPublicFor
                assistantId = goal.assistantId
                parentObjectCreatorId = goal.creatorId
                title = goal.extendedName
                followObjectsType = FOLLOWER_GOALS_TYPE
            })
            break
        case 'skills':
            await getSkillData(projectId, objectId).then(skill => {
                if (!skill) return
                object = skill
                isPublicFor = skill.isPublicFor
                assistantId = skill.assistantId
                parentObjectCreatorId = skill.userId
                title = skill.extendedName
                followObjectsType = FOLLOWER_SKILLS_TYPE
            })
            break
        case 'artifacts':
            await getArtifactData(projectId, objectId).then(artifact => {
                if (!artifact) return
                object = artifact
                isPublicFor = artifact.isPublicFor
                assistantId = artifact.assistantId
                parentObjectCreatorId = artifact.userId || artifact.creatorId
                title = artifact.title
                followObjectsType = FOLLOWER_ARTIFACTS_TYPE
            })
            break
        case 'assistants':
            const assistant = getAssistantInProject(projectId, objectId)
            if (!assistant) break
            object = assistant
            isPublicFor = [FEED_PUBLIC_FOR_ALL]
            assistantId = objectId
            parentObjectCreatorId = assistant.creatorId
            title = assistant.displayName
            followObjectsType = FOLLOWER_ASSISTANTS_TYPE
            break
    }

    return { isPublicFor, assistantId, followObjectsType, object, parentObjectCreatorId, title }
}

export const getParentObjectName = async (projectId, objectId, objectType) => {
    let name = ''

    switch (objectType) {
        case 'tasks':
            const task = await getTaskData(projectId, objectId)
            if (task) name = task.extendedName
            break
        case 'notes':
            const note = await getNote(projectId, objectId)
            if (note) name = note.extendedTitle
            break
        case 'topics':
            const chat = await getChatMeta(projectId, objectId)
            if (chat) name = chat.title
            break
        case 'users':
        case 'contacts':
            const user = TasksHelper.getUserInProject(projectId, objectId)
            const contact = user || TasksHelper.getContactInProject(projectId, objectId)
            if (contact) name = contact.displayName
            break
        case 'goals':
            const goal = await getGoalData(projectId, objectId)
            if (goal) name = goal.extendedName
            break
        case 'skills':
            const skill = await getSkillData(projectId, objectId)
            if (skill) name = skill.extendedName
            break
        case 'artifacts':
            const artifact = await getArtifactData(projectId, objectId)
            if (artifact) name = artifact.title
            break
        case 'assistants':
            const assistant = getAssistantInProject(projectId, objectId)
            if (assistant) name = assistant.displayName
            break
    }

    return name
}

const updateParentObjectAssistantIfNeeded = (projectId, assistantId, objectId, objectType) => {
    const resolvedAssistantId = resolveAssistantForProjectObject(projectId, assistantId)?.uid
    if (resolvedAssistantId && resolvedAssistantId !== assistantId) {
        const needGenerateUpdate = !!assistantId

        switch (objectType) {
            case 'tasks':
                setTaskAssistant(projectId, objectId, resolvedAssistantId, needGenerateUpdate)
                break
            case 'notes':
                setNoteAssistant(projectId, objectId, resolvedAssistantId, needGenerateUpdate)
                break
            case 'chats':
            case 'topics':
                updateChatAssistant(projectId, objectId, resolvedAssistantId)
                break
            case 'contacts':
                const isUser = !!TasksHelper.getUserInProject(projectId, objectId)
                isUser
                    ? setUserAssistant(projectId, objectId, resolvedAssistantId, needGenerateUpdate)
                    : setContactAssistant(projectId, objectId, resolvedAssistantId, needGenerateUpdate)
                break
            case 'users':
                setUserAssistant(projectId, objectId, resolvedAssistantId, needGenerateUpdate)
                break
            case 'goals':
                setGoalAssistant(projectId, objectId, resolvedAssistantId, needGenerateUpdate)
                break
            case 'skills':
                setSkillAssistant(projectId, objectId, resolvedAssistantId, needGenerateUpdate)
                break
            case 'artifacts':
                setArtifactAssistant(projectId, objectId, resolvedAssistantId)
                break
        }

        return resolvedAssistantId
    }
    return assistantId
}

const updateLastAssistantCommentData = async (
    projectId,
    objectId,
    objectType,
    creatorId,
    followerIds,
    assistantId,
    batch
) => {
    const activeFollowerIds = filterActiveProjectUserIds(projectId, followerIds, 'lastAssistantCommentData')

    activeFollowerIds.forEach(followerId => {
        const updateData = { objectType, objectId, creatorId, creatorType: 'user', date: moment().utc().valueOf() }
        updateUserDataDirectly(
            followerId,
            {
                [`lastAssistantCommentData.${projectId}`]: updateData,
                [`lastAssistantCommentData.${ASSISTANT_LAST_COMMENT_ALL_PROJECTS_KEY}`]: {
                    ...updateData,
                    projectId,
                },
                ...(assistantId
                    ? {
                          [`lastAssistantCommentDataByAssistant.${assistantId}.${projectId}`]: updateData,
                          [`lastAssistantCommentDataByAssistant.${assistantId}.${ASSISTANT_LAST_COMMENT_ALL_PROJECTS_KEY}`]:
                              {
                                  ...updateData,
                                  projectId,
                              },
                      }
                    : {}),
            },
            batch,
            projectId
        )
    })
}

const storeComment = async (
    projectId,
    objectId,
    objectType,
    commentId,
    comment,
    commentType,
    editingCommentId,
    userIdsToNotify,
    creatorId,
    followerIds,
    title,
    chatMembers,
    assistantId,
    sharedBatch = null,
    created = Date.now(),
    assistantRunRequest = null
) => {
    const batch = sharedBatch || new BatchWrapper(getDb())
    const mediaContext = extractMediaContextFromText(comment)
    const notificationIdentity = buildCommentNotificationIdentity({
        projectId,
        chatType: objectType,
        objectId,
        commentId,
        creatorId,
    })
    if (isNewComment(editingCommentId)) {
        queueObjectActivityFeedUnreadClear(getDb(), batch, {
            projectId,
            userId: creatorId,
            objectType,
            objectId,
        })

        const followrsMap = {}
        followerIds.forEach(uid => {
            followrsMap[uid] = true
        })
        userIdsToNotify.forEach(userId => {
            batch.set(getDb().doc(`chatNotifications/${projectId}/${userId}/${commentId}`), {
                ...notificationIdentity,
                chatId: objectId,
                followed: !!followrsMap[userId],
                date: moment().utc().valueOf(),
                creatorType: 'user',
            })
        })

        generatePushAndEmailNotifcations(
            notificationIdentity,
            comment,
            followerIds.filter(uid => uid !== creatorId),
            title,
            batch
        )

        updateLastAssistantCommentData(projectId, objectId, objectType, creatorId, followerIds, assistantId, batch)
    }
    batch.set(
        getDb().doc(`chatComments/${projectId}/${objectType}/${objectId}/comments/${commentId}`),
        editingCommentId
            ? { commentText: comment, mediaContext }
            : {
                  commentText: comment,
                  mediaContext,
                  lastChangeDate: getFirestoreTime(),
                  created,
                  creatorId,
                  fromAssistant: false,
                  ...(objectType === 'tasks' && { commentType }),
                  ...(assistantRunRequest && { assistantRunRequest }),
              },
        { merge: true }
    )

    if (!sharedBatch) await batch.commit()
}

const generatePushAndEmailNotifcations = (notificationIdentity, comment, followerIds, title, batch) => {
    if (followerIds.length > 0) {
        const { projectId, chatType: objectType, objectId } = notificationIdentity
        const messageTimestamp = Date.now()
        sendChatPushNotification(notificationIdentity, comment, followerIds, title, messageTimestamp, batch)
        batch.set(
            getDb().doc(`emailNotifications/${objectId}`),
            {
                ...notificationIdentity,
                userIds: firebase.firestore.FieldValue.arrayUnion(...followerIds),
                objectType: objectType === 'topics' ? 'chats' : objectType,
                objectName: TasksHelper.getTaskNameWithoutMeta(title),
                messageTimestamp,
            },
            { merge: true }
        )
    }
}

const earnGoldInCommunitesWhenComment = (projectId, editingCommentId, isPublicFor, creatorId) => {
    const { loggedUser } = store.getState()
    if (
        !editingCommentId &&
        isPublicFor.includes(FEED_PUBLIC_FOR_ALL) &&
        ProjectHelper.getTypeOfProject(loggedUser, projectId) === PROJECT_TYPE_GUIDE
    ) {
        earnGold(projectId, creatorId, MAX_GOLD_TO_EARN_BY_COMMENT, 'buttonContainerId')
    }
}

const getFollowerLists = async (projectId, objectType, objectId, creatorId, oldComment, comment, isPublicFor) => {
    let parentFollowerIds = await getParentObjectFollowerIds(projectId, objectType, objectId)
    parentFollowerIds = isPublicFor.includes(FEED_PUBLIC_FOR_ALL)
        ? parentFollowerIds
        : parentFollowerIds.filter(uid => isPublicFor.includes(uid))

    let newFollowerIds = [creatorId]

    const mentionedUserIdsInOldComment = oldComment ? TasksHelper.getMentionIdsFromTitle(oldComment) : []
    const mentionedUserIdsInNewComment = TasksHelper.getMentionIdsFromTitle(comment)
    const newMentionedUserIdsInComment = difference(mentionedUserIdsInNewComment, mentionedUserIdsInOldComment)

    newFollowerIds.push(...newMentionedUserIdsInComment)

    newFollowerIds = uniq(newFollowerIds)

    newFollowerIds = isPublicFor.includes(FEED_PUBLIC_FOR_ALL)
        ? newFollowerIds
        : newFollowerIds.filter(uid => isPublicFor.includes(uid))

    newFollowerIds = difference(newFollowerIds, parentFollowerIds)

    const followerIds = [...parentFollowerIds, ...newFollowerIds]

    return { followerIds, newFollowerIds, newMentionIds: newMentionedUserIdsInComment }
}

const queueParentCommentPreview = (batch, projectId, type, objectId, preview, commentType, isLatestComment) => {
    // Global assistants have no writable project-local parent document. Their
    // per-project conversation metadata lives in the chat document above.
    if (type === 'assistants' && isGlobalAssistant(objectId)) return
    const data = {
        ...(isLatestComment
            ? {
                  'commentsData.lastComment': shrinkTagText(preview, LAST_COMMENT_CHARACTER_LIMIT_IN_BIG_SCREEN),
                  'commentsData.lastCommentType': commentType,
              }
            : {}),
        'commentsData.amount': firebase.firestore.FieldValue.increment(1),
    }
    if ((type === 'contacts' || type === 'users') && TasksHelper.getUserInProject(projectId, objectId)) {
        const userData = Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key.replace('commentsData.', `commentsData.${projectId}.`),
                value,
            ])
        )
        updateUserDataDirectly(objectId, userData, batch, projectId)
        return
    }
    const paths = {
        tasks: `items/${projectId}/tasks/${objectId}`,
        goals: `goals/${projectId}/items/${objectId}`,
        notes: `noteItems/${projectId}/notes/${objectId}`,
        contacts: `projectsContacts/${projectId}/contacts/${objectId}`,
        users: `projectsContacts/${projectId}/contacts/${objectId}`,
        skills: `skills/${projectId}/items/${objectId}`,
        assistants: `assistants/${projectId}/items/${objectId}`,
        artifacts: `artifactItems/${projectId}/artifacts/${objectId}`,
    }
    if (paths[type]) batch.update(getDb().doc(paths[type]), data)
}

const pendingAssistantCallbacks = new Map()

export async function createObjectMessage(
    projectId,
    objectId,
    comment,
    objectType,
    commentType,
    editingCommentId,
    oldComment,
    skipAssistantTrigger = false,
    explicitAssistantEnabled = null,
    explicitAssistantId = null,
    onAssistantRunSettled = null,
    submission = null
) {
    // Editing an existing message retains the existing edit path. New comments
    // acquire their permanent ID and disk copy before any preliminary read.
    if (editingCommentId) {
        return sendObjectMessage(
            projectId,
            objectId,
            comment,
            objectType,
            commentType,
            editingCommentId,
            oldComment,
            skipAssistantTrigger,
            explicitAssistantEnabled,
            explicitAssistantId,
            onAssistantRunSettled
        )
    }
    const userId = store.getState().loggedUser?.uid
    if (!userId) throw new Error('Sign in before sending a comment.')
    if (!projectId || !objectId || !objectType || typeof comment !== 'string' || !comment.trim())
        throw new Error('A comment and its destination are required.')
    const id = submission?.id || getId()
    const entry = commentOutbox.enqueue({
        id,
        ...(submission?.created && { created: submission.created }),
        userId,
        projectId,
        objectId,
        comment,
        objectType,
        commentType,
        skipAssistantTrigger,
        explicitAssistantEnabled,
        explicitAssistantId,
        // A caller that skips the trigger here because it starts the assistant run itself.
        ...(submission?.requestsAssistantRun === true && { requestsAssistantRun: true }),
        // A caller that already waited for the thread's previous answer (Anna, AT-2922).
        ...(submission?.threadGateCleared === true && { threadGateCleared: true }),
    })
    if (onAssistantRunSettled) pendingAssistantCallbacks.set(id, onAssistantRunSettled)
    // The return value means locally saved. The regular message list renders
    // this ID with its pending/failed state until delivery is acknowledged.
    commentOutbox.flushOne(entry).catch(() => {})
    return id
}

export const sendQueuedObjectMessage = (entry, isActive, controls = null) =>
    sendObjectMessage(
        entry.projectId,
        entry.objectId,
        entry.comment,
        entry.objectType,
        entry.commentType,
        null,
        null,
        entry.skipAssistantTrigger,
        entry.explicitAssistantEnabled,
        entry.explicitAssistantId,
        result => {
            pendingAssistantCallbacks.get(entry.id)?.(result)
            pendingAssistantCallbacks.delete(entry.id)
        },
        entry,
        isActive,
        controls
    )

const removedByUserError = () =>
    Object.assign(new Error('The queued message was removed.'), { code: 'removed-by-user' })

// AT-2922: hold a message that asks the assistant for an answer until the thread's previous answer
// (including a VM job it started there) is complete. The hold happens before anything is written,
// so the message enters the thread after that answer, never between it and its question.
const holdUntilThreadIsFree = (controls, entry, thread, isActive) =>
    controls.hold(() =>
        threadRunGate.waitForThreadIdle(thread, {
            isActive,
            excludeId: entry.id,
            originalCreated: entry.created,
            shouldCancel: () => !commentOutbox.read(entry.userId, entry.id),
            shouldRelease: () => commentOutbox.read(entry.userId, entry.id)?.forceSend === true,
            subscribe: subscribeCommentOutbox,
            onQueued: () => commentOutbox.update(entry.userId, entry.id, { status: 'queued' }),
        })
    )

async function sendObjectMessage(
    projectId,
    objectId,
    comment,
    objectType,
    commentType,
    editingCommentId,
    oldComment,
    skipAssistantTrigger = false,
    explicitAssistantEnabled = null,
    explicitAssistantId = null,
    onAssistantRunSettled = null,
    queuedEntry = null,
    isActive = () => true,
    controls = null
) {
    const promises = []
    promises.push(getParentObjectData(projectId, objectId, objectType))
    promises.push(getChatMeta(projectId, objectId))
    const [parentData, chat] = await Promise.all(promises)
    if (!isActive()) throw Object.assign(new Error('Account changed'), { code: 'cancelled' })
    let { isPublicFor, assistantId, followObjectsType, object, parentObjectCreatorId, title } = parentData

    // The assistant selected when the user submitted the message is authoritative for this run.
    // Persisting an assistant change and sending a message are separate network operations, so
    // re-reading only the parent object here can otherwise race and route the answer to the old
    // project assistant.
    assistantId = explicitAssistantId || assistantId

    if (object) {
        const { loggedUser } = store.getState()
        const { uid: creatorId } = loggedUser
        // Last-resort fallback for `isThreadAssistantEnabled` below. Scoped so a flag armed for a
        // different chat (e.g. a `skipNavigation: true` pre-config run) cannot trigger an
        // assistant answer in this one (AT-2084).
        const assistantEnabled = selectAssistantEnabledFor(store.getState(), projectId, objectId)

        const chatIsAlreadyCreated = !!chat
        const commentId = editingCommentId || queuedEntry?.id || getId()

        // Debug logging for webhook task detection
        if (__DEV__) {
            console.log('🔍 WEBHOOK DEBUG: Checking if webhook task:', {
                objectType,
                objectId,
                hasObject: !!object,
                hasTaskMetadata: !!object?.taskMetadata,
                isWebhookTask: object?.taskMetadata?.isWebhookTask,
                taskMetadata: object?.taskMetadata,
                editingCommentId,
            })
        }

        const userIdsToNotify = filterActiveProjectUserIds(
            projectId,
            generateUserIdsToNotifyForNewComments(projectId, isPublicFor, creatorId),
            'commentNotifications'
        )

        const {
            followerIds: unfilteredFollowerIds,
            newFollowerIds: unfilteredNewFollowerIds,
            newMentionIds,
        } = await getFollowerLists(projectId, objectType, objectId, creatorId, oldComment, comment, isPublicFor)
        const followerIds = filterActiveProjectUserIds(projectId, unfilteredFollowerIds, 'commentFollowers')
        const activeFollowerIdsMap = followerIds.reduce((map, userId) => {
            map[userId] = true
            return map
        }, {})
        const newFollowerIds = unfilteredNewFollowerIds.filter(userId => activeFollowerIdsMap[userId])

        if (!isActive()) throw Object.assign(new Error('Account changed'), { code: 'cancelled' })
        const originalAssistantId = assistantId
        assistantId = queuedEntry
            ? resolveAssistantForProjectObject(projectId, assistantId)?.uid || assistantId
            : updateParentObjectAssistantIfNeeded(projectId, assistantId, objectId, objectType)

        const isWebhookTask = objectType === 'tasks' && object?.taskMetadata?.isWebhookTask
        const isThreadAssistantEnabled =
            explicitAssistantEnabled !== null
                ? explicitAssistantEnabled
                : object
                  ? object.isAssistantEnabled === true
                  : assistantEnabled
        const triggersAssistantRun =
            !editingCommentId && isThreadAssistantEnabled && !isWebhookTask && !skipAssistantTrigger
        // The assistant answers only if this page gets its askToBot request out. Recording the run on
        // the comment lets the server start it when the page goes away first, e.g. a phone discarding
        // the tab while the callable cold-starts (functions/Assistant/assistantMissedRunCatchUp.js).
        const assistantRunRequest =
            triggersAssistantRun || queuedEntry?.requestsAssistantRun === true
                ? { projectId, objectType, objectId, assistantId: assistantId || null }
                : null

        if (queuedEntry && assistantRunRequest && controls?.hold && !queuedEntry.threadGateCleared) {
            const outcome = await holdUntilThreadIsFree(
                controls,
                queuedEntry,
                { projectId, objectType, objectId },
                isActive
            )
            if (outcome.status === 'cancelled') {
                onAssistantRunSettled?.({ status: 'removed' })
                throw removedByUserError()
            }
            commentOutbox.update(queuedEntry.userId, queuedEntry.id, { status: 'pending', created: outcome.created })
            const released = {
                ...(commentOutbox.read(queuedEntry.userId, queuedEntry.id) || queuedEntry),
                created: outcome.created,
                // In memory only: a failed delivery waits for its turn again on retry.
                threadGateCleared: true,
            }
            // After a real wait everything read above (followers, members, the thread's assistant)
            // may be stale, so the message is prepared again from scratch.
            if (outcome.waited)
                return sendObjectMessage(
                    projectId,
                    objectId,
                    comment,
                    objectType,
                    commentType,
                    editingCommentId,
                    oldComment,
                    skipAssistantTrigger,
                    explicitAssistantEnabled,
                    explicitAssistantId,
                    onAssistantRunSettled,
                    released,
                    isActive,
                    controls
                )
            queuedEntry = released
        }

        const promises = []
        if (queuedEntry) {
            const committed = await commitCommentOnce(getDb(), {
                commentPath: `chatComments/${projectId}/${objectType}/${objectId}/comments/${commentId}`,
                chatPath: `chatObjects/${projectId}/chats/${objectId}`,
                isActive,
                stageWrites: async (batch, currentChat) => {
                    await storeComment(
                        projectId,
                        objectId,
                        objectType,
                        commentId,
                        comment,
                        commentType,
                        null,
                        userIdsToNotify,
                        creatorId,
                        followerIds,
                        title,
                        currentChat ? uniq([...(currentChat.members || []), creatorId]) : [creatorId],
                        assistantId,
                        batch,
                        queuedEntry.created,
                        assistantRunRequest
                    )
                    const preview = cleanTextMetaData(removeFormatTagsFromText(comment), true).trim() || 'Comment'
                    const isLatestComment = !currentChat || !(currentChat.lastEditionDate > queuedEntry.created)
                    const commentsData = {
                        lastCommentOwnerId: creatorId,
                        lastComment: preview,
                        lastCommentType: commentType,
                        amount: firebase.firestore.FieldValue.increment(1),
                    }
                    const chatRef = getDb().doc(`chatObjects/${projectId}/chats/${objectId}`)
                    if (currentChat) {
                        batch.update(chatRef, {
                            members: firebase.firestore.FieldValue.arrayUnion(creatorId),
                            ...(isLatestComment
                                ? { commentsData, lastEditionDate: queuedEntry.created, lastEditorId: creatorId }
                                : { 'commentsData.amount': firebase.firestore.FieldValue.increment(1) }),
                        })
                    } else {
                        batch.set(chatRef, {
                            id: objectId,
                            title,
                            type: objectType,
                            members: [creatorId],
                            commentsData,
                            lastEditionDate: queuedEntry.created,
                            lastEditorId: creatorId,
                            creatorId: parentObjectCreatorId,
                            created: queuedEntry.created,
                            isPublicFor,
                            hasStar: '#ffffff',
                            usersFollowing: followerIds,
                            quickDateId: '',
                            assistantId,
                            stickyData: { days: 0, stickyEndDate: 0 },
                        })
                    }
                    batch.update(getDb().doc(`projects/${projectId}`), { lastChatActionDate: Date.now() })
                    queueParentCommentPreview(
                        batch,
                        projectId,
                        objectType,
                        objectId,
                        preview,
                        commentType,
                        isLatestComment
                    )
                },
            })
            if (!committed) {
                onAssistantRunSettled?.({ status: 'already-saved' })
                return commentId
            }
        } else {
            promises.push(
                storeComment(
                    projectId,
                    objectId,
                    objectType,
                    commentId,
                    comment,
                    commentType,
                    editingCommentId,
                    userIdsToNotify,
                    creatorId,
                    followerIds,
                    title,
                    chat ? uniq([...chat.members, creatorId]) : [creatorId],
                    assistantId
                )
            )

            if (chatIsAlreadyCreated) {
                promises.push(updateChatWhenAddComment(projectId, creatorId, comment, commentType, objectId))
            } else {
                promises.push(
                    createChat(
                        objectId,
                        projectId,
                        creatorId,
                        comment,
                        objectType,
                        title,
                        isPublicFor,
                        '#ffffff',
                        null,
                        followerIds,
                        '',
                        assistantId,
                        commentType,
                        parentObjectCreatorId
                    )
                )
            }
        }

        if (!isActive()) throw Object.assign(new Error('Account changed'), { code: 'cancelled' })
        if (queuedEntry) updateParentObjectAssistantIfNeeded(projectId, originalAssistantId, objectId, objectType)

        // Check if this is a webhook task and trigger webhook with the user's message
        if (!editingCommentId && objectType === 'tasks' && object?.taskMetadata?.isWebhookTask) {
            console.log('🌐 WEBHOOK MESSAGE: Detected message in webhook task, triggering webhook:', {
                taskId: objectId,
                webhookUrl: object.taskMetadata.webhookUrl,
                messageLength: comment.length,
            })
            // Trigger webhook execution asynchronously
            runHttpsCallableFunction('executeWebhookForMessage', {
                userId: creatorId,
                projectId,
                objectId,
                prompt: comment,
                taskMetadata: object.taskMetadata,
                userIdsToNotify: filterActiveProjectUserIds(
                    projectId,
                    generateUserIdsToNotifyForNewComments(projectId, isPublicFor, creatorId),
                    'webhookTaskNotifications'
                ),
                isPublicFor,
                assistantId,
            })
                .then(result => {
                    console.log('🌐 WEBHOOK MESSAGE: Webhook triggered successfully:', result)
                })
                .catch(error => {
                    console.error('🌐 WEBHOOK MESSAGE: Error triggering webhook:', error)
                    alert(`Webhook failed: ${error.message}`)
                })
        }

        newFollowerIds.forEach(uid => {
            const user = TasksHelper.getUserInProject(projectId, uid)
            if (user) {
                const followData = {
                    followObjectsType,
                    followObjectId: objectId,
                    feedCreator: user,
                    followObject: object,
                }
                tryAddFollower(projectId, followData, null)
            }
        })

        if (!editingCommentId) {
            earnGoldInCommunitesWhenComment(projectId, editingCommentId, isPublicFor, creatorId)
            logEvent('new_chat_message', {
                id: commentId,
                objectId: objectId,
                objectType: objectType,
            })
        }

        createGenericTaskWhenMention(projectId, objectId, newMentionIds, GENERIC_COMMENT_TYPE, objectType, assistantId)

        try {
            // Offline these acks never arrive, so awaiting them left the comment
            // modal open forever on a comment that was in fact already stored
            // locally and queued for the server. The writes stay durable in the
            // persisted mutation queue; only the wait is skipped (AT-2340).
            await awaitWriteAck(Promise.all(promises), 'createObjectMessage comment')
            if (!queuedEntry)
                await awaitWriteAck(
                    updateLastCommentData(projectId, editingCommentId, objectId, objectType, comment, commentType),
                    'createObjectMessage last-comment preview'
                )
        } catch (error) {
            console.error('[TaskComments] Failed to persist comment before updating preview metadata', {
                projectId,
                objectId,
                objectType,
                editingCommentId: !!editingCommentId,
                error: error.message,
            })
            throw error
        }

        // Only trigger regular AI assistant if not a webhook task and not explicitly skipped
        if (__DEV__) {
            console.log('🔍 [TIMING] CLIENT: Checking assistant trigger conditions', {
                editingCommentId,
                isThreadAssistantEnabled,
                isWebhookTask,
                skipAssistantTrigger,
                objectType,
                hasTaskMetadata: !!object?.taskMetadata,
            })
        }
        if (triggersAssistantRun) {
            const clientSubmissionTime = Date.now()
            const clientSubmissionTimestamp = new Date().toISOString()
            if (__DEV__) {
                console.log('⏱️ [TIMING] CLIENT: User message submitted, calling askToBotSecondGen', {
                    timestamp: clientSubmissionTimestamp,
                    submissionTime: clientSubmissionTime,
                    submissionTimeISO: clientSubmissionTimestamp,
                    userId: creatorId,
                    messageId: commentId,
                    projectId,
                    objectType,
                    objectId,
                    assistantId,
                })
            }
            const functionCallStartTime = Date.now()
            const runThread = { projectId, objectType, objectId }
            // The next queued message in this thread waits until this request has settled (AT-2922).
            threadRunGate.beginThreadRun(runThread, commentId)
            runHttpsCallableFunction(
                'askToBotSecondGen',
                {
                    userId: creatorId,
                    messageId: commentId,
                    projectId,
                    objectType,
                    objectId,
                    userIdsToNotify: [...userIdsToNotify, creatorId],
                    isPublicFor,
                    language: window.navigator.language,
                    assistantId,
                    followerIds,
                },
                {
                    timeout: 540000, // 9 minutes to match backend timeout
                }
            )
                .then(result => {
                    threadRunGate.settleThreadRun(runThread, commentId)
                    endBotSpinnerWait(projectId, objectId)
                    onAssistantRunSettled?.({ status: result?.cancelled ? 'cancelled' : 'completed', result })
                    const clientCallCompleteTime = Date.now()
                    const totalClientToServerTime = clientCallCompleteTime - clientSubmissionTime
                    const networkLatency = functionCallStartTime - clientSubmissionTime
                    if (__DEV__) {
                        console.log('⏱️ [TIMING] CLIENT: askToBotSecondGen call initiated successfully', {
                            timestamp: new Date().toISOString(),
                            submissionTime: clientSubmissionTime,
                            submissionTimeISO: clientSubmissionTimestamp,
                            completionTime: clientCallCompleteTime,
                            completionTimeISO: new Date().toISOString(),
                            timeSinceSubmission: `${totalClientToServerTime}ms`,
                            networkLatency: `${networkLatency}ms`,
                            backendProcessingTime: `${totalClientToServerTime - networkLatency}ms`,
                            result,
                        })
                    }
                })
                .catch(error => {
                    threadRunGate.settleThreadRun(runThread, commentId)
                    endBotSpinnerWait(projectId, objectId)
                    onAssistantRunSettled?.({ status: 'failed', error })
                    console.error('⏱️ [TIMING] CLIENT: Error calling askToBotSecondGen', {
                        error: error.message,
                        timestamp: new Date().toISOString(),
                        submissionTime: clientSubmissionTime,
                        submissionTimeISO: clientSubmissionTimestamp,
                        timeSinceSubmission: `${Date.now() - clientSubmissionTime}ms`,
                    })
                })
        } else {
            if (isThreadAssistantEnabled) endBotSpinnerWait(projectId, objectId)
            onAssistantRunSettled?.({ status: 'skipped' })
        }

        return commentId
    }

    if (queuedEntry)
        throw Object.assign(new Error('The object is not available. Your comment remains saved locally.'), {
            // Redux-backed contacts/assistants may still be loading after resume.
            // Absence here is not an authoritative server deletion.
            code: 'unavailable',
        })
    return null
}

export const createChat = async (
    chatId,
    projectId,
    creatorId,
    comment,
    type,
    title,
    isPublicFor,
    hasStar,
    stickyData,
    followerIds,
    quickDateId,
    assistantId,
    commentType,
    parentObjectCreatorId
) => {
    // Get the usersFollowing list before creating the chat
    const usersFollowing = followerIds || (await getChatFollowerIds(projectId, title, type, chatId))

    const chat = {
        id: chatId,
        title,
        type,
        members: firebase.firestore.FieldValue.arrayUnion(creatorId),
        lastEditionDate: Date.now(),
        lastEditorId: creatorId,
        commentsData: comment
            ? {
                  lastCommentOwnerId: creatorId,
                  lastComment: cleanTextMetaData(removeFormatTagsFromText(comment), true).trim() || 'Comment',
                  lastCommentType: commentType,
                  amount: firebase.firestore.FieldValue.increment(1),
              }
            : null,
        hasStar,
        creatorId: parentObjectCreatorId,
        isPublicFor,
        created: moment().valueOf(),
        usersFollowing,
        quickDateId: quickDateId ? quickDateId : '',
        assistantId,
        ...(stickyData ? { stickyData } : { stickyData: { days: 0, stickyEndDate: 0 } }),
    }

    const promises = []
    promises.push(getDb().doc(`chatObjects/${projectId}/chats/${chatId}`).set(chat))
    promises.push(setProjectLastChatActionDate(projectId))
    promises.push(logEvent('new_chat', { id: chatId }))
    promises.push(addFollowersToChat(projectId, title, type, chatId, usersFollowing))
    await Promise.all(promises)

    return chat
}

const updateChatWhenAddComment = async (projectId, creatorId, cleanedComment, commentType, chatId) => {
    const updateData = {
        members: firebase.firestore.FieldValue.arrayUnion(creatorId),
        commentsData: null,
    }

    updateData.commentsData = {
        lastCommentOwnerId: creatorId,
        lastComment: cleanTextMetaData(removeFormatTagsFromText(cleanedComment), true).trim() || 'Comment',
        lastCommentType: commentType,
        amount: firebase.firestore.FieldValue.increment(1),
    }

    updateChatData(projectId, chatId, updateData, null)
    setProjectLastChatActionDate(projectId)
}

const getLastComment = async (projectId, objectType, objectId) => {
    const docs = (
        await getDb()
            .collection(`chatComments/${projectId}/${objectType}/${objectId}/comments`)
            .orderBy('lastChangeDate', 'desc')
            .limit(1)
            .get()
    ).docs
    return docs.length > 0 ? { ...docs[0].data(), id: docs[0].id } : null
}

const updateLastCommentData = async (projectId, editingCommentId, objectId, objectType, comment, commentType) => {
    if (editingCommentId) {
        const lastComment = await getLastComment(projectId, objectType, objectId)
        if (lastComment && lastComment.id === editingCommentId) {
            await updateLastCommentDataOfChatParentObject(projectId, objectId, objectType, comment, commentType)
        }
    } else {
        await updateLastCommentDataOfChatParentObject(projectId, objectId, objectType, comment, commentType)
    }
}

const updateLastCommentDataOfChatParentObject = async (projectId, objectId, type, lastComment, commentType) => {
    const parsedComment = cleanTextMetaData(removeFormatTagsFromText(lastComment), true).trim()
    const cleanedComment = shrinkTagText(parsedComment || 'Comment', LAST_COMMENT_CHARACTER_LIMIT_IN_BIG_SCREEN)

    if (type === 'assistants') {
        await updateAssistantLastCommentData(projectId, objectId, cleanedComment, commentType)
    } else if (type === 'contacts') {
        if (TasksHelper.getUserInProject(projectId, objectId)) {
            updateUserLastCommentData(projectId, objectId, cleanedComment, commentType)
        } else if (TasksHelper.getContactInProject(projectId, objectId)) {
            updateContactLastCommentData(projectId, objectId, cleanedComment, commentType)
        }
    } else if (type === 'skills') {
        await updateSkillLastCommentData(projectId, objectId, cleanedComment, commentType)
    } else if (type === 'tasks') {
        await updateTaskLastCommentData(projectId, objectId, cleanedComment, commentType)
    } else if (type === 'goals') {
        await updateGoalLastCommentData(projectId, objectId, cleanedComment, commentType)
    } else if (type === 'notes') {
        await updateNoteLastCommentData(projectId, objectId, cleanedComment, commentType)
    } else if (type === 'artifacts') {
        await updateArtifactLastCommentData(projectId, objectId, cleanedComment, commentType)
    }
}

export const repairChatMetadata = async (projectId, objectId, type) => {
    if (type === 'assistants') {
        await resetAssistantLastCommentData(projectId, objectId)
    } else if (type === 'contacts') {
        if (TasksHelper.getUserInProject(projectId, objectId)) {
            resetUserLastCommentData(projectId, objectId)
        } else if (TasksHelper.getContactInProject(projectId, objectId)) {
            resetContactLastCommentData(projectId, objectId)
        }
    } else if (type === 'skills') {
        await resetSkillLastCommentData(projectId, objectId)
    } else if (type === 'tasks') {
        await rebuildTaskLastCommentData(projectId, objectId)
    } else if (type === 'goals') {
        await resetGoalLastCommentData(projectId, objectId)
    } else if (type === 'notes') {
        await resetNoteLastCommentData(projectId, objectId)
    } else if (type === 'artifacts') {
        await resetArtifactLastCommentData(projectId, objectId)
    }
}

const sendChatPushNotification = async (
    notificationIdentity,
    message,
    followerIds,
    objectName,
    messageTimestamp,
    batch
) => {
    const { projectId, chatType: objectType, objectId } = notificationIdentity
    const { displayName: userName } = store.getState().loggedUser

    const project = ProjectHelper.getProjectById(projectId)
    const cleanedComment = TasksHelper.getTaskNameWithoutMeta(message)
    batch.set(getDb().doc(`pushNotifications/${notificationIdentity.commentId}`), {
        ...notificationIdentity,
        userIds: followerIds,
        body: `${project.name}\n  ✔ ${objectName}\n ${userName} ${'commented'}: ${cleanedComment}`,
        link: getLinkedParentChatUrl(projectId, objectType, objectId),
        messageTimestamp,
        type: 'Chat Notification',
        chatId: objectId,
    })
}

const addFollowersToChat = async (projectId, chatName, objectType, chatId, followerIds) => {
    const usersFollowing = followerIds || (await getChatFollowerIds(projectId, chatName, objectType, chatId))

    const batch = new BatchWrapper(getDb())
    if (__DEV__) {
        console.log('addFollowersToChat parameters:', {
            projectId,
            chatName,
            objectType,
            chatId,
            followerIds,
            usersFollowing,
        })
    }
    // maybe we wanted to not have feeds because it's a chat and then we dont want updates
    if (objectType === 'topics') {
        usersFollowing.forEach(userId => {
            addFollowerWithoutFeeds(projectId, userId, objectType, chatId, null, batch)
        })
    }
    batch.update(getDb().doc(`chatObjects/${projectId}/chats/${chatId}`), { usersFollowing })
    await batch.commit()
}

const getParentObjectFollowerIds = async (projectId, objectType, chatId) => {
    const doc = await getDb().doc(`followers/${projectId}/${objectType}/${chatId}`).get()
    const data = doc.data()
    const followersFromCollection = data ? data.usersFollowing : []

    // If no followers in the collection yet, try reading from the chat object itself
    if (followersFromCollection.length === 0 && objectType === 'topics') {
        const chatDoc = await getDb().doc(`chatObjects/${projectId}/chats/${chatId}`).get()
        const chat = chatDoc.data()
        if (chat && chat.usersFollowing && chat.usersFollowing.length > 0) {
            return chat.usersFollowing
        }
    }

    return followersFromCollection
}

const getChatFollowerIds = async (projectId, chatName, objectType, chatId) => {
    if (objectType === 'topics') {
        const mentionedUserIds = getMentionedUsersIdsWhenEditText(chatName, '')
        const userId = store.getState().loggedUser.uid
        return uniq([userId, ...mentionedUserIds])
    } else {
        return await getParentObjectFollowerIds(projectId, objectType, chatId)
    }
}

async function setFollowChatNotifications(projectId, userId, chatId, followed, batch) {
    // chatNotifications is a private per-user inbox. A client may update its
    // own existing entries, but adding a mentioned/assigned teammate as a
    // follower must not query that teammate's inbox from this browser.
    if (store.getState().loggedUser?.uid !== userId) return

    const docs = await getDb()
        .collection(`chatNotifications/${projectId}/${userId}`)
        .where('chatId', '==', chatId)
        .get()
    docs.forEach(doc => {
        batch.update(getDb().doc(`chatNotifications/${projectId}/${userId}/${doc.id}`), { followed })
        queueSummaryChatNotificationPatch(batch, userId, projectId, doc.id, { followed })
    })
}

export async function addFollowerToChat(projectId, chatId, userId) {
    const db = getDb()
    const doc = await db.doc(`chatObjects/${projectId}/chats/${chatId}`).get()
    if (doc.exists) {
        const chat = doc.data()
        const { usersFollowing } = chat
        if (!usersFollowing.includes(userId)) {
            const batch = new BatchWrapper(db)
            batch.update(db.doc(`chatObjects/${projectId}/chats/${chatId}`), {
                usersFollowing: firebase.firestore.FieldValue.arrayUnion(userId),
            })
            await setFollowChatNotifications(projectId, userId, chatId, true, batch)
            await batch.commit()
        }
    }
}

export async function removeFollowerFromChat(projectId, chatId, userId) {
    const db = getDb()
    const doc = await db.doc(`chatObjects/${projectId}/chats/${chatId}`).get()
    if (doc.exists) {
        const chat = doc.data()
        const { usersFollowing } = chat
        if (usersFollowing.includes(userId)) {
            const batch = new BatchWrapper(db)
            batch.update(db.doc(`chatObjects/${projectId}/chats/${chatId}`), {
                usersFollowing: firebase.firestore.FieldValue.arrayRemove(userId),
            })
            await setFollowChatNotifications(projectId, userId, chatId, false, batch)
            await batch.commit()
        }
    }
}

export {
    getFollowedAndUnfollowedChatNotificationsAmount,
    resetNotificationsWhenUserHasAnActiveChat,
} from '../../notificationBadges'

export const getNewFollowedAndUnfollowedCommentsAmountInProjectList = (projectChatNotifications, projectIdsList) => {
    let globalTotalFollowed = 0
    let globalTotalUnfollowed = 0
    const projectIds = Object.keys(projectChatNotifications)
    projectIds.forEach(projectId => {
        const needToCount = projectIdsList.includes(projectId)
        if (needToCount) {
            const { totalFollowed, totalUnfollowed } = projectChatNotifications[projectId]
            globalTotalFollowed += totalFollowed
            globalTotalUnfollowed += totalUnfollowed
        }
    })
    return { totalFollowed: globalTotalFollowed, totalUnfollowed: globalTotalUnfollowed }
}

export { markChatMessagesAsRead, markMessagesAsRead } from './chatReadNotifications'
